import React, { useState, useEffect, useRef } from "react";

// ===== ANIMATION HOOK =====
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold: 0.1, ...options }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

// ===== ANIMATED COMPONENT =====
const Reveal = ({ children, delay = 0, direction = "up" }) => {
  const [ref, isInView] = useInView();
  const directions = {
    up: "translateY(40px)",
    down: "translateY(-40px)",
    left: "translateX(40px)",
    right: "translateX(-40px)",
    none: "translateY(0)",
  };

  return (
    <div
      ref={ref}
      style={{
        transform: isInView ? "translate(0)" : directions[direction],
        opacity: isInView ? 1 : 0,
        transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

// ===== COUNTER ANIMATION =====
const AnimatedCounter = ({ end, suffix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [ref, isInView] = useInView();

  useEffect(() => {
    if (!isInView) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
};

// ===== NAVIGATION =====
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const links = [
    { name: "Accueil", href: "#home" },
    { name: "À Propos", href: "#about" },
    { name: "Solutions", href: "#solutions" },
    { name: "Projets", href: "#projects" },
    { name: "Processus", href: "#process" },
    { name: "Investir", href: "#invest" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        width: "100%",
        zIndex: 1000,
        padding: scrolled ? "12px 24px" : "20px 24px",
        background: scrolled ? "rgba(10, 22, 40, 0.95)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.1)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <a href="#home" style={{ textDecoration: "none" }}>
          <div style={{ fontSize: "28px", fontWeight: 800 }}>
            <span style={{ color: "#fff" }}>H</span>
            <span style={{ color: "#34d399" }}>2</span>
            <span style={{ color: "#fff" }}>SOL</span>
          </div>
        </a>

        {/* Desktop Menu */}
        <div
          style={{
            display: "flex",
            gap: "32px",
            alignItems: "center",
          }}
          className="desktop-menu"
        >
          {links.map((link) => (
            <a
              key={link.name}
              href={link.href}
              style={{
                color: "rgba(255,255,255,0.8)",
                textDecoration: "none",
                fontSize: "14px",
                fontWeight: 500,
                transition: "color 0.2s ease",
                letterSpacing: "0.02em",
              }}
              onMouseOver={(e) => (e.target.style.color = "#34d399")}
              onMouseOut={(e) =>
                (e.target.style.color = "rgba(255,255,255,0.8)")
              }
            >
              {link.name}
            </a>
          ))}
          <a
            href="#contact"
            style={{
              padding: "10px 24px",
              background: "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
              color: "#fff",
              textDecoration: "none",
              borderRadius: "50px",
              fontSize: "14px",
              fontWeight: 600,
              transition: "all 0.3s ease",
            }}
          >
            Nous Contacter
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{
            display: "none",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "8px",
          }}
          className="mobile-menu-btn"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
          >
            {mobileOpen ? (
              <path d="M18 6L6 18M6 6l12 12" />
            ) : (
              <path d="M3 12h18M3 6h18M3 18h18" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "rgba(10, 22, 40, 0.98)",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {links.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              style={{
                color: "#fff",
                textDecoration: "none",
                fontSize: "16px",
                padding: "12px 0",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              {link.name}
            </a>
          ))}
        </div>
      )}
    </nav>
  );
};

// ===== HERO SECTION =====
const Hero = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section
      id="home"
      style={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Image */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "url(https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?w=1920&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: `translateY(${scrollY * 0.3}px)`,
          transition: "transform 0.1s ease-out",
        }}
      />

      {/* Overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg, rgba(10, 22, 40, 0.92) 0%, rgba(15, 40, 71, 0.88) 50%, rgba(19, 78, 74, 0.85) 100%)",
        }}
      />

      {/* Animated gradient orbs */}
      <div
        style={{
          position: "absolute",
          width: "800px",
          height: "800px",
          background:
            "radial-gradient(circle, rgba(52, 211, 153, 0.08) 0%, transparent 60%)",
          top: "-300px",
          right: "-300px",
          animation: "float 20s ease-in-out infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "600px",
          height: "600px",
          background:
            "radial-gradient(circle, rgba(59, 130, 246, 0.06) 0%, transparent 60%)",
          bottom: "-200px",
          left: "-200px",
          animation: "float 25s ease-in-out infinite reverse",
        }}
      />

      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "0 24px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "60px",
          alignItems: "center",
          position: "relative",
          zIndex: 10,
          width: "100%",
          paddingTop: "100px",
        }}
      >
        <div>
          <Reveal delay={0.2}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 20px",
                background: "rgba(52, 211, 153, 0.1)",
                border: "1px solid rgba(52, 211, 153, 0.3)",
                borderRadius: "50px",
                marginBottom: "24px",
              }}
            >
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  background: "#34d399",
                  borderRadius: "50%",
                  animation: "pulse 2s infinite",
                }}
              />
              <span
                style={{
                  color: "#34d399",
                  fontSize: "13px",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                Pionnier de l'Hydrogène Vert
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <h1
              style={{
                fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
                fontWeight: 800,
                margin: "0 0 24px",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                color: "#fff",
              }}
            >
              L'Énergie Propre
              <br />
              <span
                style={{
                  color: "#34d399",
                  textShadow: "0 0 80px rgba(52, 211, 153, 0.4)",
                }}
              >
                de Demain
              </span>
            </h1>
          </Reveal>

          <Reveal delay={0.6}>
            <p
              style={{
                fontSize: "18px",
                color: "rgba(255,255,255,0.75)",
                maxWidth: "520px",
                margin: "0 0 32px",
                lineHeight: 1.8,
              }}
            >
              H2SOL-Industry développe des solutions innovantes de production
              d'hydrogène vert par électrolyse, alimentée par des énergies
              renouvelables. Notre mission : accélérer la transition vers la
              neutralité carbone.
            </p>
          </Reveal>

          <Reveal delay={0.8}>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <a
                href="#projects"
                style={{
                  padding: "16px 32px",
                  background:
                    "linear-gradient(135deg, #34d399 0%, #059669 100%)",
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: "50px",
                  fontWeight: 600,
                  fontSize: "15px",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 30px rgba(52, 211, 153, 0.3)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                Découvrir nos Projets
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
              <a
                href="#about"
                style={{
                  padding: "16px 32px",
                  background: "rgba(255,255,255,0.1)",
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: "50px",
                  fontWeight: 600,
                  fontSize: "15px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  transition: "all 0.3s ease",
                }}
              >
                En Savoir Plus
              </a>
            </div>
          </Reveal>

          <Reveal delay={1}>
            <div
              style={{
                display: "flex",
                gap: "40px",
                marginTop: "48px",
                paddingTop: "32px",
                borderTop: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: 800,
                    color: "#34d399",
                  }}
                >
                  <AnimatedCounter end={100} suffix="%" />
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.6)",
                    marginTop: "4px",
                  }}
                >
                  Énergie Verte
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: 800,
                    color: "#34d399",
                  }}
                >
                  <AnimatedCounter end={0} />
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.6)",
                    marginTop: "4px",
                  }}
                >
                  Émission CO₂
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: 800,
                    color: "#34d399",
                  }}
                >
                  <AnimatedCounter end={2050} />
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.6)",
                    marginTop: "4px",
                  }}
                >
                  Objectif Neutralité
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal direction="left" delay={0.5}>
          <div
            style={{
              position: "relative",
              borderRadius: "24px",
              overflow: "hidden",
            }}
          >
            <img
              src="https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?w=800&q=80"
              alt="Énergie renouvelable"
              style={{
                width: "100%",
                height: "500px",
                objectFit: "cover",
                borderRadius: "24px",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: "24px",
                left: "24px",
                right: "24px",
                background: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(10px)",
                padding: "24px",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  background:
                    "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                  borderRadius: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2"
                >
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    color: "#0f172a",
                    fontSize: "17px",
                  }}
                >
                  Énergie 100% Renouvelable
                </div>
                <div
                  style={{
                    color: "#64748b",
                    fontSize: "14px",
                    marginTop: "2px",
                  }}
                >
                  Solaire, éolien et hydraulique combinés
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Scroll indicator */}
      <div
        style={{
          position: "absolute",
          bottom: "40px",
          left: "50%",
          transform: "translateX(-50%)",
          animation: "bounce 2s infinite",
          zIndex: 10,
        }}
      >
        <svg width="24" height="40" viewBox="0 0 24 40" fill="none">
          <rect
            x="1"
            y="1"
            width="22"
            height="38"
            rx="11"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="2"
          />
          <circle
            cx="12"
            cy="12"
            r="4"
            fill="#34d399"
            style={{ animation: "scrollDot 2s infinite" }}
          />
        </svg>
      </div>
    </section>
  );
};

// ===== PARTNERS BAR =====
const PartnersBar = () => {
  const partners = [
    "Green Deal UE",
    "ADEME",
    "Collectivité Territoriale",
    "CNRS",
    "Tamoil",
  ];

  return (
    <section
      style={{
        background: "#fff",
        padding: "40px 24px",
        borderBottom: "1px solid #e2e8f0",
        width: "100%",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "60px",
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            color: "#94a3b8",
            fontSize: "13px",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
          }}
        >
          Ils nous font confiance
        </span>
        {partners.map((partner, i) => (
          <span
            key={i}
            style={{
              color: "#64748b",
              fontSize: "18px",
              fontWeight: 700,
              opacity: 0.6,
            }}
          >
            {partner}
          </span>
        ))}
      </div>
    </section>
  );
};

// ===== ABOUT SECTION =====
const About = () => {
  return (
    <section
      id="about"
      style={{
        background: "#fff",
        padding: "120px 24px",
        width: "100%",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
            gap: "80px",
            alignItems: "center",
          }}
        >
          <div>
            <Reveal>
              <span
                style={{
                  color: "#059669",
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.15em",
                }}
              >
                Notre Mission
              </span>
            </Reveal>

            <Reveal delay={0.2}>
              <h2
                style={{
                  fontSize: "clamp(2rem, 4vw, 3.2rem)",
                  fontWeight: 700,
                  color: "#0f172a",
                  margin: "16px 0 24px",
                  lineHeight: 1.15,
                }}
              >
                L'Hydrogène au Cœur
                <br />
                <span style={{ color: "#059669" }}>des Enjeux Climatiques</span>
              </h2>
            </Reveal>

            <Reveal delay={0.3}>
              <p
                style={{
                  fontSize: "17px",
                  color: "#475569",
                  lineHeight: 1.85,
                  marginBottom: "20px",
                }}
              >
                En décembre 2019, la Commission européenne a dévoilé son{" "}
                <strong>Green Deal</strong> pour rendre l'Europe climatiquement
                neutre d'ici 2050. H2SOL-Industry s'inscrit pleinement dans
                cette ambition avec des solutions concrètes de production
                d'hydrogène décarboné.
              </p>
            </Reveal>

            <Reveal delay={0.4}>
              <p
                style={{
                  fontSize: "17px",
                  color: "#475569",
                  lineHeight: 1.85,
                  marginBottom: "24px",
                }}
              >
                Notre nom reflète notre approche :{" "}
                <strong style={{ color: "#059669" }}>H</strong> pour Hydro
                power,
                <strong style={{ color: "#059669" }}> H</strong> pour Hydrogène,
                <strong style={{ color: "#059669" }}> Sol</strong> pour Solar.
                En stabilisant l'hydrogène à l'état fluide à température et
                pression normales, nous créons un vecteur énergétique dense et
                compétitif par rapport au charbon.
              </p>
            </Reveal>

            <Reveal delay={0.5}>
              <div
                style={{
                  background:
                    "linear-gradient(135deg, rgba(5, 150, 105, 0.08) 0%, rgba(13, 148, 136, 0.08) 100%)",
                  borderRadius: "16px",
                  padding: "24px",
                  borderLeft: "4px solid #059669",
                }}
              >
                <p
                  style={{
                    fontSize: "16px",
                    color: "#0f172a",
                    lineHeight: 1.7,
                    margin: 0,
                    fontStyle: "italic",
                  }}
                >
                  "L'hydrogène produit par électrolyse est pur et normal. Notre
                  hydrogène 100% vert peut être utilisé comme seule source
                  d'énergie ou co-alimenté avec du gaz naturel."
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.6}>
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  marginTop: "32px",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 20px",
                    background: "#f8fafc",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#059669"
                    strokeWidth="2"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span
                    style={{
                      fontWeight: 600,
                      color: "#0f172a",
                      fontSize: "14px",
                    }}
                  >
                    Zéro Émission
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 20px",
                    background: "#f8fafc",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#059669"
                    strokeWidth="2"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span
                    style={{
                      fontWeight: 600,
                      color: "#0f172a",
                      fontSize: "14px",
                    }}
                  >
                    100% Renouvelable
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 20px",
                    background: "#f8fafc",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#059669"
                    strokeWidth="2"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span
                    style={{
                      fontWeight: 600,
                      color: "#0f172a",
                      fontSize: "14px",
                    }}
                  >
                    Prix Compétitif
                  </span>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal direction="left" delay={0.3}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              <img
                src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600&q=80"
                alt="Panneaux solaires"
                style={{
                  width: "100%",
                  height: "240px",
                  objectFit: "cover",
                  borderRadius: "16px",
                }}
              />
              <img
                src="https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?w=600&q=80"
                alt="Éoliennes"
                style={{
                  width: "100%",
                  height: "240px",
                  objectFit: "cover",
                  borderRadius: "16px",
                  marginTop: "40px",
                }}
              />
              <img
                src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600&q=80"
                alt="Énergie hydraulique"
                style={{
                  width: "100%",
                  height: "240px",
                  objectFit: "cover",
                  borderRadius: "16px",
                  marginTop: "-40px",
                }}
              />
              <img
                src="https://images.unsplash.com/photo-1558449028-b53a39d100fc?w=600&q=80"
                alt="Infrastructure énergétique"
                style={{
                  width: "100%",
                  height: "240px",
                  objectFit: "cover",
                  borderRadius: "16px",
                }}
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ===== STATS SECTION =====
const Stats = () => {
  const stats = [
    {
      value: 21,
      suffix: "MW",
      label: "Puissance Installée",
      desc: "Capacité de la centrale",
    },
    {
      value: 40,
      suffix: "GWh",
      label: "Production Annuelle",
      desc: "Électricité générée",
    },
    {
      value: 60,
      suffix: "%",
      label: "Rendement",
      desc: "Avec récupération de chaleur",
    },
    { value: 3000, suffix: "t", label: "CO₂ Évitées", desc: "Par an" },
  ];

  return (
    <section
      style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
        padding: "100px 24px",
        position: "relative",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "url(https://images.unsplash.com/photo-1517925035435-7976539b920d?w=1920&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.1,
        }}
      />

      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "60px" }}>
          <Reveal>
            <h2
              style={{
                fontSize: "clamp(2rem, 4vw, 3rem)",
                fontWeight: 700,
                color: "#fff",
                margin: "0 0 16px",
              }}
            >
              Nos Chiffres Clés
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p
              style={{
                color: "rgba(255,255,255,0.7)",
                maxWidth: "600px",
                margin: "0 auto",
                fontSize: "17px",
              }}
            >
              Des performances concrètes pour un impact environnemental
              mesurable
            </p>
          </Reveal>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "24px",
          }}
        >
          {stats.map((stat, i) => (
            <Reveal key={i} delay={i * 0.15}>
              <div
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "20px",
                  padding: "40px 32px",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                }}
              >
                <div
                  style={{
                    fontSize: "48px",
                    fontWeight: 800,
                    color: "#34d399",
                    lineHeight: 1,
                  }}
                >
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#fff",
                    marginTop: "12px",
                  }}
                >
                  {stat.label}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "rgba(255,255,255,0.5)",
                    marginTop: "4px",
                  }}
                >
                  {stat.desc}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===== SOLUTIONS SECTION =====
const Solutions = () => {
  const solutions = [
    {
      icon: "☀️",
      title: "Énergie Solaire",
      desc: "Panneaux photovoltaïques haute performance pour une production maximale d'électricité propre. Orientation et inclinaison optimisées pour chaque région.",
      features: [
        "Production continue",
        "Maintenance réduite",
        "Durée de vie 25+ ans",
      ],
    },
    {
      icon: "💨",
      title: "Énergie Éolienne",
      desc: "Turbines éoliennes de nouvelle génération pour capturer l'énergie du vent. Fonctionnement jour et nuit pour une production constante.",
      features: ["24h/24 opérationnel", "Haute efficacité", "Faible empreinte"],
    },
    {
      icon: "💧",
      title: "Énergie Hydraulique",
      desc: "Système STEP (Station de Transfert d'Énergie par Pompage) pour stocker l'énergie excédentaire et la restituer à la demande.",
      features: ["Stockage massif", "Réponse rapide", "Rendement 80%+"],
    },
    {
      icon: "⚡",
      title: "Électrolyse",
      desc: "Électrolyseurs alcalins de pointe pour séparer l'hydrogène de l'eau. Consommation optimisée à moins de 10 kWh par Nm³ d'H₂.",
      features: ["Pureté 99.99%", "Scalable", "Automatisé"],
    },
  ];

  return (
    <section
      id="solutions"
      style={{
        background: "linear-gradient(180deg, #f8fafc 0%, #fff 100%)",
        padding: "120px 24px",
        width: "100%",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "70px" }}>
          <Reveal>
            <span
              style={{
                color: "#059669",
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.15em",
              }}
            >
              Nos Solutions
            </span>
          </Reveal>
          <Reveal delay={0.2}>
            <h2
              style={{
                fontSize: "clamp(2rem, 4vw, 3rem)",
                fontWeight: 700,
                color: "#0f172a",
                margin: "16px 0",
              }}
            >
              Technologies de Production
            </h2>
          </Reveal>
          <Reveal delay={0.3}>
            <p
              style={{
                color: "#64748b",
                maxWidth: "650px",
                margin: "0 auto",
                lineHeight: 1.7,
                fontSize: "17px",
              }}
            >
              Un écosystème complet combinant plusieurs sources d'énergies
              renouvelables pour une production d'hydrogène vert optimale et
              continue.
            </p>
          </Reveal>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "28px",
          }}
        >
          {solutions.map((sol, i) => (
            <Reveal key={i} delay={i * 0.15}>
              <div
                style={{
                  background: "#fff",
                  borderRadius: "24px",
                  padding: "36px",
                  boxShadow: "0 4px 30px rgba(0,0,0,0.06)",
                  border: "1px solid #e2e8f0",
                  height: "100%",
                  transition: "all 0.3s ease",
                  cursor: "default",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.boxShadow =
                    "0 20px 50px rgba(0,0,0,0.1)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 30px rgba(0,0,0,0.06)";
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background:
                      "linear-gradient(135deg, rgba(5, 150, 105, 0.1) 0%, rgba(13, 148, 136, 0.1) 100%)",
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "32px",
                    marginBottom: "24px",
                  }}
                >
                  {sol.icon}
                </div>
                <h3
                  style={{
                    fontSize: "22px",
                    fontWeight: 700,
                    color: "#0f172a",
                    margin: "0 0 12px",
                  }}
                >
                  {sol.title}
                </h3>
                <p
                  style={{
                    color: "#64748b",
                    fontSize: "15px",
                    lineHeight: 1.7,
                    marginBottom: "20px",
                  }}
                >
                  {sol.desc}
                </p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {sol.features.map((f, j) => (
                    <div
                      key={j}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        fontSize: "14px",
                        color: "#475569",
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#059669"
                        strokeWidth="2.5"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===== HYDROGEN TYPES =====
const HydrogenTypes = () => {
  const types = [
    {
      color: "#6b7280",
      name: "Gris",
      method: "Vapoformage du méthane",
      emission: "9.3 kg CO₂/kg H₂",
      percent: "95%",
      desc: "Procédé le plus répandu mais très polluant. Chauffe le méthane à 800°C pour extraire l'hydrogène.",
    },
    {
      color: "#3b82f6",
      name: "Bleu",
      method: "Avec capture CO₂",
      emission: "Réduit (~2kg)",
      percent: "3%",
      desc: "Capture le CO₂ émis pour l'enfouir. Solution intermédiaire mais coûteuse.",
    },
    {
      color: "#06b6d4",
      name: "Turquoise",
      method: "Pyrolyse nucléaire",
      emission: "Variable",
      percent: "1%",
      desc: "Utilise l'énergie nucléaire pour chauffer le méthane à très haute température (1000-2000°C).",
    },
    {
      color: "#10b981",
      name: "Vert",
      method: "Électrolyse renouvelable",
      emission: "0 kg CO₂",
      percent: "1%",
      desc: "La seule solution véritablement décarbonée. C'est notre spécialité chez H2SOL.",
      highlight: true,
    },
  ];

  return (
    <section
      style={{
        background: "#fff",
        padding: "120px 24px",
        width: "100%",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
            gap: "80px",
            alignItems: "start",
          }}
        >
          <div>
            <Reveal>
              <span
                style={{
                  color: "#059669",
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.15em",
                }}
              >
                Classification
              </span>
            </Reveal>
            <Reveal delay={0.2}>
              <h2
                style={{
                  fontSize: "clamp(2rem, 4vw, 3rem)",
                  fontWeight: 700,
                  color: "#0f172a",
                  margin: "16px 0 24px",
                  lineHeight: 1.15,
                }}
              >
                Les Couleurs de
                <br />
                <span style={{ color: "#059669" }}>l'Hydrogène</span>
              </h2>
            </Reveal>
            <Reveal delay={0.3}>
              <p
                style={{
                  fontSize: "17px",
                  color: "#475569",
                  lineHeight: 1.8,
                  marginBottom: "24px",
                }}
              >
                L'hydrogène n'est pas une source d'énergie primaire mais un
                vecteur énergétique. Sa couleur dépend de son mode de production
                et de son impact environnemental.
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <p
                style={{
                  fontSize: "17px",
                  color: "#475569",
                  lineHeight: 1.8,
                  marginBottom: "32px",
                }}
              >
                Aujourd'hui, <strong>95% de l'hydrogène mondial</strong> est
                produit à partir d'énergies fossiles. H2SOL s'engage à inverser
                cette tendance avec une production 100% verte.
              </p>
            </Reveal>

            <Reveal delay={0.5}>
              <img
                src="https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=800&q=80"
                alt="Production hydrogène"
                style={{
                  width: "100%",
                  height: "300px",
                  objectFit: "cover",
                  borderRadius: "20px",
                }}
              />
            </Reveal>
          </div>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            {types.map((type, i) => (
              <Reveal key={i} delay={i * 0.1} direction="left">
                <div
                  style={{
                    background: type.highlight
                      ? "linear-gradient(135deg, #059669 0%, #0d9488 100%)"
                      : "#f8fafc",
                    borderRadius: "20px",
                    padding: "28px",
                    border: type.highlight ? "none" : "1px solid #e2e8f0",
                    boxShadow: type.highlight
                      ? "0 20px 50px rgba(5, 150, 105, 0.25)"
                      : "none",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {type.highlight && (
                    <div
                      style={{
                        position: "absolute",
                        top: "16px",
                        right: "16px",
                        background: "rgba(255,255,255,0.2)",
                        padding: "6px 14px",
                        borderRadius: "50px",
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#fff",
                      }}
                    >
                      Notre Solution
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "start",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        background: type.highlight
                          ? "rgba(255,255,255,0.2)"
                          : `${type.color}15`,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          width: "24px",
                          height: "24px",
                          background: type.highlight ? "#fff" : type.color,
                          borderRadius: "50%",
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "8px",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "20px",
                            fontWeight: 700,
                            color: type.highlight ? "#fff" : "#0f172a",
                            margin: 0,
                          }}
                        >
                          Hydrogène {type.name}
                        </h3>
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: type.highlight
                              ? "rgba(255,255,255,0.8)"
                              : "#64748b",
                          }}
                        >
                          {type.percent} mondial
                        </span>
                      </div>
                      <p
                        style={{
                          color: type.highlight
                            ? "rgba(255,255,255,0.9)"
                            : "#64748b",
                          fontSize: "14px",
                          lineHeight: 1.6,
                          margin: "0 0 12px",
                        }}
                      >
                        {type.desc}
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            padding: "6px 12px",
                            background: type.highlight
                              ? "rgba(255,255,255,0.15)"
                              : "#fff",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: 500,
                            color: type.highlight ? "#fff" : type.color,
                            border: type.highlight
                              ? "none"
                              : `1px solid ${type.color}30`,
                          }}
                        >
                          {type.method}
                        </span>
                        <span
                          style={{
                            padding: "6px 12px",
                            background: type.highlight
                              ? "rgba(255,255,255,0.15)"
                              : "#fff",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: type.highlight ? "#fff" : type.color,
                            border: type.highlight
                              ? "none"
                              : `1px solid ${type.color}30`,
                          }}
                        >
                          {type.emission}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ===== PROJECTS SECTION =====
const Projects = () => {
  return (
    <section
      id="projects"
      style={{
        background:
          "linear-gradient(135deg, #0a1628 0%, #0f2847 50%, #134e4a 100%)",
        padding: "120px 24px",
        position: "relative",
        overflow: "hidden",
        width: "100%",
      }}
    >
      {/* Background image */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "url(https://images.unsplash.com/photo-1548337138-e87d889cc369?w=1920&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.08,
        }}
      />

      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "70px" }}>
          <Reveal>
            <span
              style={{
                color: "#34d399",
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.15em",
              }}
            >
              Nos Projets
            </span>
          </Reveal>
          <Reveal delay={0.2}>
            <h2
              style={{
                fontSize: "clamp(2rem, 4vw, 3rem)",
                fontWeight: 700,
                color: "#fff",
                margin: "16px 0",
              }}
            >
              Projets en Développement
            </h2>
          </Reveal>
        </div>

        {/* Saint-Pierre Project */}
        <div
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "32px",
            padding: "48px",
            marginBottom: "40px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
              gap: "60px",
              alignItems: "center",
            }}
          >
            <Reveal>
              <div>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 16px",
                    background: "rgba(52, 211, 153, 0.15)",
                    borderRadius: "50px",
                    marginBottom: "20px",
                  }}
                >
                  <span style={{ fontSize: "18px" }}>🇫🇷</span>
                  <span
                    style={{
                      color: "#34d399",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    Territoire Français
                  </span>
                </div>

                <h3
                  style={{
                    fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
                    fontWeight: 700,
                    color: "#fff",
                    margin: "0 0 20px",
                    lineHeight: 1.2,
                  }}
                >
                  Projet Pilote
                  <br />
                  <span style={{ color: "#34d399" }}>
                    Saint-Pierre et Miquelon
                  </span>
                </h3>

                <p
                  style={{
                    fontSize: "16px",
                    color: "rgba(255,255,255,0.75)",
                    lineHeight: 1.8,
                    marginBottom: "24px",
                  }}
                >
                  En collaboration avec des investisseurs locaux et avec le
                  soutien de la Collectivité Territoriale et des maires de Saint
                  Pierre et Miquelon, H2SOL déploie un projet pilote d'hydrogène
                  vert innovant. Ce projet s'inscrit dans le cadre d'une société
                  à mission.
                </p>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "16px",
                    marginBottom: "24px",
                  }}
                >
                  {[
                    { val: "21 MW", label: "Puissance électrique" },
                    { val: "40 GWh", label: "Production annuelle" },
                    { val: "90%", label: "Population alimentée" },
                    { val: "1000t", label: "Fuel économisé/an" },
                  ].map((s, i) => (
                    <div
                      key={i}
                      style={{
                        background: "rgba(255,255,255,0.05)",
                        borderRadius: "12px",
                        padding: "16px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: 800,
                          color: "#34d399",
                        }}
                      >
                        {s.val}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "rgba(255,255,255,0.6)",
                        }}
                      >
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  {[
                    "Collectivité Territoriale",
                    "ADEME",
                    "Conseil Territorial",
                  ].map((p, i) => (
                    <span
                      key={i}
                      style={{
                        padding: "8px 16px",
                        background: "rgba(52, 211, 153, 0.1)",
                        border: "1px solid rgba(52, 211, 153, 0.3)",
                        borderRadius: "50px",
                        color: "#34d399",
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal direction="left" delay={0.3}>
              <div style={{ position: "relative" }}>
                <img
                  src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80"
                  alt="Saint-Pierre et Miquelon"
                  style={{
                    width: "100%",
                    height: "400px",
                    objectFit: "cover",
                    borderRadius: "20px",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "20px",
                    left: "20px",
                    background: "rgba(0,0,0,0.7)",
                    backdropFilter: "blur(10px)",
                    padding: "16px 20px",
                    borderRadius: "12px",
                  }}
                >
                  <div
                    style={{ color: "#fff", fontWeight: 600, fontSize: "15px" }}
                  >
                    📍 Saint-Pierre et Miquelon
                  </div>
                  <div
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: "13px",
                      marginTop: "4px",
                    }}
                  >
                    Archipel de l'Atlantique Nord
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Other projects grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
            gap: "24px",
          }}
        >
          <Reveal delay={0.2}>
            <div
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "24px",
                overflow: "hidden",
              }}
            >
              <img
                src="https://images.unsplash.com/photo-1518173946687-a4c036bc10a8?w=600&q=80"
                alt="Europe"
                style={{ width: "100%", height: "200px", objectFit: "cover" }}
              />
              <div style={{ padding: "28px" }}>
                <span
                  style={{
                    display: "inline-block",
                    padding: "6px 12px",
                    background: "rgba(59, 130, 246, 0.2)",
                    borderRadius: "50px",
                    color: "#60a5fa",
                    fontSize: "12px",
                    fontWeight: 600,
                    marginBottom: "12px",
                  }}
                >
                  En planification
                </span>
                <h4
                  style={{
                    color: "#fff",
                    fontSize: "20px",
                    fontWeight: 700,
                    margin: "0 0 12px",
                  }}
                >
                  Expansion Européenne
                </h4>
                <p
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "14px",
                    lineHeight: 1.7,
                    margin: 0,
                  }}
                >
                  Création d'usines H2SOL-Industry dans plusieurs pays européens
                  pour répondre aux objectifs du Green Deal.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <div
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "24px",
                overflow: "hidden",
              }}
            >
              <img
                src="https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=600&q=80"
                alt="Afrique"
                style={{ width: "100%", height: "200px", objectFit: "cover" }}
              />
              <div style={{ padding: "28px" }}>
                <span
                  style={{
                    display: "inline-block",
                    padding: "6px 12px",
                    background: "rgba(251, 191, 36, 0.2)",
                    borderRadius: "50px",
                    color: "#fbbf24",
                    fontSize: "12px",
                    fontWeight: 600,
                    marginBottom: "12px",
                  }}
                >
                  Vision 2030
                </span>
                <h4
                  style={{
                    color: "#fff",
                    fontSize: "20px",
                    fontWeight: 700,
                    margin: "0 0 12px",
                  }}
                >
                  Région MEA
                </h4>
                <p
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "14px",
                    lineHeight: 1.7,
                    margin: 0,
                  }}
                >
                  Développement de projets au Moyen-Orient et en Afrique,
                  régions à fort potentiel solaire et éolien.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ===== PROCESS SECTION =====
const Process = () => {
  const steps = [
    {
      num: "01",
      title: "Captation Énergétique",
      desc: "Les panneaux solaires et éoliennes captent l'énergie renouvelable disponible en continu.",
      icon: "☀️",
    },
    {
      num: "02",
      title: "Stockage STEP",
      desc: "L'énergie excédentaire est stockée via le système de pompage-turbinage hydraulique.",
      icon: "💧",
    },
    {
      num: "03",
      title: "Électrolyse",
      desc: "L'électricité alimente les électrolyseurs qui séparent H₂ et O₂ de l'eau purifiée.",
      icon: "⚡",
    },
    {
      num: "04",
      title: "Compression",
      desc: "L'hydrogène est compressé à 500-900 bars pour un stockage haute densité.",
      icon: "🔄",
    },
    {
      num: "05",
      title: "Liquéfaction",
      desc: "Refroidissement cryogénique pour obtenir de l'hydrogène liquide ultra-dense.",
      icon: "❄️",
    },
    {
      num: "06",
      title: "Distribution",
      desc: "L'hydrogène vert est prêt à être utilisé ou transporté vers les clients.",
      icon: "🚀",
    },
  ];

  return (
    <section
      id="process"
      style={{
        background: "#fff",
        padding: "120px 24px",
        width: "100%",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "70px" }}>
          <Reveal>
            <span
              style={{
                color: "#059669",
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.15em",
              }}
            >
              Comment ça marche
            </span>
          </Reveal>
          <Reveal delay={0.2}>
            <h2
              style={{
                fontSize: "clamp(2rem, 4vw, 3rem)",
                fontWeight: 700,
                color: "#0f172a",
                margin: "16px 0",
              }}
            >
              Notre Processus de Production
            </h2>
          </Reveal>
          <Reveal delay={0.3}>
            <p
              style={{
                color: "#64748b",
                maxWidth: "650px",
                margin: "0 auto",
                lineHeight: 1.7,
                fontSize: "17px",
              }}
            >
              De la captation d'énergie renouvelable à la livraison d'hydrogène
              vert, découvrez les étapes de notre chaîne de production.
            </p>
          </Reveal>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "24px",
          }}
        >
          {steps.map((step, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: "20px",
                  padding: "32px",
                  border: "1px solid #e2e8f0",
                  height: "100%",
                  position: "relative",
                  transition: "all 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = "#059669";
                  e.currentTarget.style.boxShadow =
                    "0 10px 40px rgba(5, 150, 105, 0.1)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "24px",
                    right: "24px",
                    fontSize: "48px",
                    fontWeight: 900,
                    color: "rgba(5, 150, 105, 0.08)",
                  }}
                >
                  {step.num}
                </div>
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    background:
                      "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "28px",
                    marginBottom: "20px",
                  }}
                >
                  {step.icon}
                </div>
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "#0f172a",
                    margin: "0 0 12px",
                  }}
                >
                  {step.title}
                </h3>
                <p
                  style={{
                    color: "#64748b",
                    fontSize: "15px",
                    lineHeight: 1.7,
                    margin: 0,
                  }}
                >
                  {step.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Technical specs */}
        <Reveal delay={0.5}>
          <div
            style={{
              marginTop: "60px",
              background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
              borderRadius: "24px",
              padding: "48px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "40px",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "14px",
                  color: "rgba(255,255,255,0.5)",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                Consommation
              </div>
              <div
                style={{ fontSize: "32px", fontWeight: 800, color: "#34d399" }}
              >
                ≤10 kWh
              </div>
              <div style={{ fontSize: "14px", color: "rgba(255,255,255,0.7)" }}>
                par Nm³ H₂
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "14px",
                  color: "rgba(255,255,255,0.5)",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                Pureté
              </div>
              <div
                style={{ fontSize: "32px", fontWeight: 800, color: "#34d399" }}
              >
                99.99%
              </div>
              <div style={{ fontSize: "14px", color: "rgba(255,255,255,0.7)" }}>
                Hydrogène pur
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "14px",
                  color: "rgba(255,255,255,0.5)",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                Stockage
              </div>
              <div
                style={{ fontSize: "32px", fontWeight: 800, color: "#34d399" }}
              >
                900 bars
              </div>
              <div style={{ fontSize: "14px", color: "rgba(255,255,255,0.7)" }}>
                Pression max
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "14px",
                  color: "rgba(255,255,255,0.5)",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                Rendement
              </div>
              <div
                style={{ fontSize: "32px", fontWeight: 800, color: "#34d399" }}
              >
                25-30%
              </div>
              <div style={{ fontSize: "14px", color: "rgba(255,255,255,0.7)" }}>
                Power-H₂-Power
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ===== ROADMAP SECTION =====
const Roadmap = () => {
  const phases = [
    {
      phase: "01",
      title: "Bureau d'Études",
      duration: "8 mois",
      status: "active",
      items: [
        "Analyse comparative mondiale",
        "Benchmarking des technologies",
        "Négociation fournisseurs",
        "Plans d'exécution détaillés",
        "Partenariat CNRS",
      ],
    },
    {
      phase: "02",
      title: "Installation Pilote",
      duration: "9-11 mois",
      status: "upcoming",
      items: [
        "Construction unité pilote",
        "Tests 5-15 maisons autonomes",
        "Validation des performances",
        "Optimisation des coûts",
        "Formation des équipes",
      ],
    },
    {
      phase: "03",
      title: "Déploiement Industriel",
      duration: "1-5 ans",
      status: "future",
      items: [
        "Projets nationaux",
        "Expansion européenne",
        "Région MEA",
        "Partenariats industriels",
        "Production de masse",
      ],
    },
  ];

  return (
    <section
      style={{
        background: "linear-gradient(180deg, #f8fafc 0%, #fff 100%)",
        padding: "120px 24px",
        width: "100%",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "70px" }}>
          <Reveal>
            <span
              style={{
                color: "#059669",
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.15em",
              }}
            >
              Planning
            </span>
          </Reveal>
          <Reveal delay={0.2}>
            <h2
              style={{
                fontSize: "clamp(2rem, 4vw, 3rem)",
                fontWeight: 700,
                color: "#0f172a",
                margin: "16px 0",
              }}
            >
              Notre Feuille de Route
            </h2>
          </Reveal>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "32px",
          }}
        >
          {phases.map((p, i) => (
            <Reveal key={i} delay={i * 0.2}>
              <div
                style={{
                  background: "#fff",
                  borderRadius: "24px",
                  padding: "36px",
                  border:
                    p.status === "active"
                      ? "2px solid #059669"
                      : "1px solid #e2e8f0",
                  boxShadow:
                    p.status === "active"
                      ? "0 20px 50px rgba(5, 150, 105, 0.15)"
                      : "0 4px 20px rgba(0,0,0,0.05)",
                  height: "100%",
                  position: "relative",
                }}
              >
                {p.status === "active" && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-12px",
                      left: "24px",
                      background:
                        "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                      padding: "6px 16px",
                      borderRadius: "50px",
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: 700,
                    }}
                  >
                    Phase actuelle
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    marginBottom: "24px",
                  }}
                >
                  <div
                    style={{
                      width: "56px",
                      height: "56px",
                      background:
                        p.status === "active"
                          ? "linear-gradient(135deg, #059669 0%, #0d9488 100%)"
                          : "#f1f5f9",
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: p.status === "active" ? "#fff" : "#64748b",
                      fontSize: "20px",
                      fontWeight: 800,
                    }}
                  >
                    {p.phase}
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: "20px",
                        fontWeight: 700,
                        color: "#0f172a",
                        margin: 0,
                      }}
                    >
                      {p.title}
                    </h3>
                    <span
                      style={{
                        color: p.status === "active" ? "#059669" : "#64748b",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      {p.duration}
                    </span>
                  </div>
                </div>

                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {p.items.map((item, j) => (
                    <li
                      key={j}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "10px 0",
                        borderBottom:
                          j < p.items.length - 1 ? "1px solid #f1f5f9" : "none",
                        color: "#475569",
                        fontSize: "15px",
                      }}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={p.status === "active" ? "#059669" : "#94a3b8"}
                        strokeWidth="2"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===== INVEST SECTION =====
const Invest = () => {
  return (
    <section
      id="invest"
      style={{
        background: "linear-gradient(135deg, #0a1628 0%, #134e4a 100%)",
        padding: "120px 24px",
        position: "relative",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "url(https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.05,
        }}
      />

      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
            gap: "60px",
            alignItems: "center",
          }}
        >
          <div>
            <Reveal>
              <span
                style={{
                  color: "#34d399",
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.15em",
                }}
              >
                Investissement
              </span>
            </Reveal>

            <Reveal delay={0.2}>
              <h2
                style={{
                  fontSize: "clamp(2rem, 4vw, 3rem)",
                  fontWeight: 700,
                  color: "#fff",
                  margin: "16px 0 24px",
                  lineHeight: 1.15,
                }}
              >
                Rejoignez la Révolution
                <br />
                <span style={{ color: "#34d399" }}>de l'Énergie Verte</span>
              </h2>
            </Reveal>

            <Reveal delay={0.3}>
              <p
                style={{
                  fontSize: "17px",
                  color: "rgba(255,255,255,0.75)",
                  lineHeight: 1.8,
                  marginBottom: "20px",
                }}
              >
                L'hydrogène représente une opportunité géoéconomique majeure.
                Les pays disposant d'énergies renouvelables abondantes
                deviendront les producteurs de demain. H2SOL vous offre
                l'opportunité de participer à cette transformation.
              </p>
            </Reveal>

            <Reveal delay={0.4}>
              <p
                style={{
                  fontSize: "17px",
                  color: "rgba(255,255,255,0.75)",
                  lineHeight: 1.8,
                  marginBottom: "32px",
                }}
              >
                La vertu et le profit sont aujourd'hui une entreprise commune.
                Investir dans H2SOL, c'est investir dans un avenir durable ET
                rentable.
              </p>
            </Reveal>

            <Reveal delay={0.5}>
              <div
                style={{
                  background: "rgba(52, 211, 153, 0.1)",
                  border: "1px solid rgba(52, 211, 153, 0.3)",
                  borderRadius: "16px",
                  padding: "24px",
                  marginBottom: "32px",
                }}
              >
                <h4
                  style={{
                    color: "#34d399",
                    fontSize: "18px",
                    fontWeight: 700,
                    margin: "0 0 16px",
                  }}
                >
                  Options d'Investissement
                </h4>
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {[
                    "Financement d'amorçage : 500 K€",
                    "Transaction in/out sur 3 à 10 ans",
                    "Équité / Prêts / Partenariats",
                    "Entrée au capital limitée à 10%",
                    "H2SOL conserve 40% de participation",
                  ].map((item, i) => (
                    <li
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "8px 0",
                        color: "rgba(255,255,255,0.85)",
                        fontSize: "15px",
                      }}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#34d399"
                        strokeWidth="2"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.6}>
              <a
                href="#contact"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "16px 32px",
                  background:
                    "linear-gradient(135deg, #34d399 0%, #059669 100%)",
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: "50px",
                  fontWeight: 600,
                  fontSize: "15px",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 30px rgba(52, 211, 153, 0.3)",
                }}
              >
                Devenir Investisseur
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </Reveal>
          </div>

          <Reveal direction="left" delay={0.3}>
            <div
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "24px",
                padding: "40px",
              }}
            >
              <h3
                style={{
                  color: "#fff",
                  fontSize: "22px",
                  fontWeight: 700,
                  margin: "0 0 32px",
                }}
              >
                Pourquoi Investir ?
              </h3>

              {[
                {
                  icon: "📈",
                  title: "Marché en Croissance",
                  desc: "Le marché de l'hydrogène devrait atteindre 200 Md€ d'ici 2030.",
                },
                {
                  icon: "🌍",
                  title: "Impact Global",
                  desc: "Contribuez à la décarbonation de l'économie mondiale.",
                },
                {
                  icon: "⚖️",
                  title: "Cadre Réglementaire",
                  desc: "Soutien politique fort via le Green Deal européen.",
                },
                {
                  icon: "💡",
                  title: "Innovation",
                  desc: "Brevets et nouvelles technologies en développement constant.",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: "16px",
                    marginBottom: i < 3 ? "24px" : 0,
                    paddingBottom: i < 3 ? "24px" : 0,
                    borderBottom:
                      i < 3 ? "1px solid rgba(255,255,255,0.1)" : "none",
                  }}
                >
                  <div
                    style={{
                      width: "50px",
                      height: "50px",
                      background: "rgba(52, 211, 153, 0.15)",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "24px",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <h4
                      style={{
                        color: "#fff",
                        fontSize: "17px",
                        fontWeight: 600,
                        margin: "0 0 6px",
                      }}
                    >
                      {item.title}
                    </h4>
                    <p
                      style={{
                        color: "rgba(255,255,255,0.7)",
                        fontSize: "14px",
                        margin: 0,
                        lineHeight: 1.6,
                      }}
                    >
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ===== FOUNDER SECTION =====
const Founder = () => {
  return (
    <section
      style={{
        background: "#fff",
        padding: "120px 24px",
        width: "100%",
      }}
    >
      <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
        <Reveal>
          <div
            style={{
              background: "linear-gradient(135deg, #f8fafc 0%, #fff 100%)",
              borderRadius: "32px",
              padding: "60px",
              border: "1px solid #e2e8f0",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "0",
                right: "0",
                width: "300px",
                height: "300px",
                background:
                  "radial-gradient(circle, rgba(5, 150, 105, 0.08) 0%, transparent 70%)",
              }}
            />

            <div
              style={{
                display: "flex",
                gap: "40px",
                alignItems: "start",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  width: "140px",
                  height: "140px",
                  background:
                    "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                  borderRadius: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "48px",
                  fontWeight: 800,
                  color: "#fff",
                  flexShrink: 0,
                }}
              >
                NP
              </div>

              <div style={{ flex: 1, minWidth: "300px" }}>
                <h3
                  style={{
                    fontSize: "28px",
                    fontWeight: 700,
                    color: "#0f172a",
                    margin: "0 0 8px",
                  }}
                >
                  Naji-Paul EL KAHY
                </h3>
                <p
                  style={{
                    color: "#059669",
                    fontSize: "16px",
                    fontWeight: 500,
                    margin: "0 0 24px",
                  }}
                >
                  Fondateur & CEO — H2SOL-Industry
                </p>

                <blockquote
                  style={{
                    fontSize: "18px",
                    color: "#475569",
                    lineHeight: 1.8,
                    margin: "0 0 24px",
                    fontStyle: "italic",
                    borderLeft: "4px solid #059669",
                    paddingLeft: "24px",
                  }}
                >
                  "Nous savons qu'il est déjà trop tard pour éviter les
                  catastrophes climatiques. Mais nous pouvons nous donner une
                  chance de réduire le choc. Si nous parvenons à fabriquer et
                  ouvrir un parachute le plus vite possible, le choc sera
                  violent mais non fatal."
                </blockquote>

                <p
                  style={{
                    fontSize: "16px",
                    color: "#64748b",
                    lineHeight: 1.8,
                    margin: 0,
                  }}
                >
                  La vertu et le profit sont aujourd'hui une entreprise commune.
                  En langage commercial, nous pouvons dire : la joint-venture
                  entre l'humanisme et le monde des affaires. Merci pour votre
                  attention au projet H2SOL-Industry.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ===== CONTACT SECTION =====
const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const response = await fetch("https://api.brevo.com/v3/smtp/email", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "api-key": "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",
        },
        body: JSON.stringify({
          sender: {
            name: "Site H2SOL",
            email: "noreply@h2sol.fr", // Email expéditeur vérifié dans Brevo
          },
          to: [
            {
              email: "contact@h2sol.fr",
              name: "H2SOL Contact",
            },
          ],
          replyTo: {
            email: formData.email,
            name: formData.name,
          },
          subject: `[H2SOL] ${formData.subject || "Nouveau message"} - ${formData.name}`,
          htmlContent: `
            <html>
              <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
                <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
                  <div style="background: linear-gradient(135deg, #059669 0%, #0d9488 100%); padding: 20px; border-radius: 10px 10px 0 0;">
                    <h1 style="color: white; margin: 0; font-size: 24px;">Nouveau message depuis le site H2SOL</h1>
                  </div>
                  <div style="background: #f8fafc; padding: 30px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 10px 10px;">
                    <table style="width: 100%; border-collapse: collapse;">
                      <tr>
                        <td style="padding: 10px 0; border-bottom: 1px solid #e2e8f0; font-weight: bold; width: 140px;">Nom :</td>
                        <td style="padding: 10px 0; border-bottom: 1px solid #e2e8f0;">${formData.name}</td>
                      </tr>
                      <tr>
                        <td style="padding: 10px 0; border-bottom: 1px solid #e2e8f0; font-weight: bold;">Email :</td>
                        <td style="padding: 10px 0; border-bottom: 1px solid #e2e8f0;"><a href="mailto:${formData.email}" style="color: #059669;">${formData.email}</a></td>
                      </tr>
                      <tr>
                        <td style="padding: 10px 0; border-bottom: 1px solid #e2e8f0; font-weight: bold;">Entreprise :</td>
                        <td style="padding: 10px 0; border-bottom: 1px solid #e2e8f0;">${formData.company || "Non spécifié"}</td>
                      </tr>
                      <tr>
                        <td style="padding: 10px 0; border-bottom: 1px solid #e2e8f0; font-weight: bold;">Sujet :</td>
                        <td style="padding: 10px 0; border-bottom: 1px solid #e2e8f0;">${formData.subject}</td>
                      </tr>
                    </table>
                    <div style="margin-top: 20px;">
                      <p style="font-weight: bold; margin-bottom: 10px;">Message :</p>
                      <div style="background: white; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
                        ${formData.message.replace(/\n/g, "<br>")}
                      </div>
                    </div>
                    <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; color: #64748b; font-size: 12px;">
                      <p>Ce message a été envoyé depuis le formulaire de contact du site H2SOL-Industry</p>
                    </div>
                  </div>
                </div>
              </body>
            </html>
          `,
        }),
      });

      if (response.ok) {
        setStatus({
          type: "success",
          message: "Message envoyé avec succès ! Nous vous répondrons rapidement.",
        });
        setFormData({ name: "", email: "", company: "", subject: "", message: "" });
      } else {
        const errorData = await response.json();
        console.error("Brevo error:", errorData);
        throw new Error(errorData.message || "Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error("Submit error:", error);
      setStatus({
        type: "error",
        message: "Une erreur est survenue. Veuillez réessayer ou nous contacter directement par email.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      style={{
        background: "linear-gradient(180deg, #f8fafc 0%, #fff 100%)",
        padding: "120px 24px",
        width: "100%",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
            gap: "60px",
          }}
        >
          <div>
            <Reveal>
              <span
                style={{
                  color: "#059669",
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.15em",
                }}
              >
                Contact
              </span>
            </Reveal>
            <Reveal delay={0.2}>
              <h2
                style={{
                  fontSize: "clamp(2rem, 4vw, 3rem)",
                  fontWeight: 700,
                  color: "#0f172a",
                  margin: "16px 0 24px",
                  lineHeight: 1.15,
                }}
              >
                Parlons de
                <br />
                <span style={{ color: "#059669" }}>Votre Projet</span>
              </h2>
            </Reveal>
            <Reveal delay={0.3}>
              <p
                style={{
                  fontSize: "17px",
                  color: "#475569",
                  lineHeight: 1.8,
                  marginBottom: "32px",
                }}
              >
                Investisseurs, collectivités, industriels — contactez-nous pour
                explorer les opportunités de partenariat et participer à la
                transition énergétique.
              </p>
            </Reveal>

            <Reveal delay={0.4}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                }}
              >
                {[
                  {
                    icon: "📍",
                    label: "Adresse",
                    value: "France • Europe • Monde",
                  },
                  { icon: "📧", label: "Email", value: "contact@h2sol.fr" },
                  {
                    icon: "📞",
                    label: "Téléphone",
                    value: "+33 6 62 32 58 38",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      padding: "20px",
                      background: "#fff",
                      borderRadius: "16px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        background:
                          "linear-gradient(135deg, rgba(5, 150, 105, 0.1) 0%, rgba(13, 148, 136, 0.1) 100%)",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "22px",
                      }}
                    >
                      {item.icon}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#64748b",
                          marginBottom: "2px",
                        }}
                      >
                        {item.label}
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "#0f172a",
                        }}
                      >
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.5}>
              <div style={{ marginTop: "32px" }}>
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                  alt="Collaboration"
                  style={{
                    width: "100%",
                    height: "200px",
                    objectFit: "cover",
                    borderRadius: "20px",
                  }}
                />
              </div>
            </Reveal>
          </div>

          <Reveal direction="left" delay={0.3}>
            <form
              onSubmit={handleSubmit}
              style={{
                background: "#fff",
                borderRadius: "24px",
                padding: "40px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 30px rgba(0,0,0,0.06)",
              }}
            >
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: 700,
                  color: "#0f172a",
                  margin: "0 0 32px",
                }}
              >
                Envoyez-nous un message
              </h3>

              {/* Message de statut */}
              {status.message && (
                <div
                  style={{
                    padding: "16px",
                    borderRadius: "12px",
                    marginBottom: "24px",
                    background: status.type === "success" ? "#ecfdf5" : "#fef2f2",
                    border: `1px solid ${status.type === "success" ? "#a7f3d0" : "#fecaca"}`,
                    color: status.type === "success" ? "#065f46" : "#991b1b",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {status.type === "success" ? "✅ " : "❌ "}
                  {status.message}
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#0f172a",
                      marginBottom: "8px",
                    }}
                  >
                    Nom *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "12px",
                      fontSize: "15px",
                      outline: "none",
                      transition: "all 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    placeholder="Votre nom"
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#0f172a",
                      marginBottom: "8px",
                    }}
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "12px",
                      fontSize: "15px",
                      outline: "none",
                      transition: "all 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#0f172a",
                    marginBottom: "8px",
                  }}
                >
                  Entreprise / Organisation
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) =>
                    setFormData({ ...formData, company: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "12px",
                    fontSize: "15px",
                    outline: "none",
                    transition: "all 0.2s ease",
                    boxSizing: "border-box",
                  }}
                  placeholder="Nom de votre entreprise"
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#0f172a",
                    marginBottom: "8px",
                  }}
                >
                  Sujet *
                </label>
                <select
                  required
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "12px",
                    fontSize: "15px",
                    outline: "none",
                    background: "#fff",
                    boxSizing: "border-box",
                  }}
                >
                  <option value="">Sélectionnez un sujet</option>
                  <option value="Investissement">Investissement</option>
                  <option value="Partenariat">Partenariat</option>
                  <option value="Nouveau projet">Nouveau projet</option>
                  <option value="Demande d'information">Demande d'information</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#0f172a",
                    marginBottom: "8px",
                  }}
                >
                  Message *
                </label>
                <textarea
                  required
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  rows={5}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "12px",
                    fontSize: "15px",
                    outline: "none",
                    resize: "vertical",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                  }}
                  placeholder="Décrivez votre projet ou votre demande..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: "100%",
                  padding: "16px",
                  background: isSubmitting 
                    ? "#94a3b8"
                    : "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: 600,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: isSubmitting ? "none" : "0 4px 20px rgba(5, 150, 105, 0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                {isSubmitting ? (
                  <>
                    <span style={{
                      width: "20px",
                      height: "20px",
                      border: "2px solid #fff",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }} />
                    Envoi en cours...
                  </>
                ) : (
                  "Envoyer le Message"
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ===== FOOTER =====
const Footer = () => {
  return (
    <footer
      style={{
        background: "linear-gradient(135deg, #0a1628 0%, #0f2847 100%)",
        padding: "80px 24px 30px",
        width: "100%",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "48px",
            paddingBottom: "60px",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "32px",
                fontWeight: 800,
                marginBottom: "16px",
              }}
            >
              <span style={{ color: "#fff" }}>H</span>
              <span style={{ color: "#34d399" }}>2</span>
              <span style={{ color: "#fff" }}>SOL</span>
            </div>
            <p
              style={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "15px",
                lineHeight: 1.7,
                marginBottom: "24px",
              }}
            >
              Pionnier de l'hydrogène vert pour accélérer la transition
              énergétique vers un avenir décarboné.
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              {["LinkedIn", "Twitter"].map((social, i) => (
                <a
                  key={i}
                  href="#"
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "rgba(255,255,255,0.1)",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    textDecoration: "none",
                    fontSize: "12px",
                    transition: "all 0.3s ease",
                  }}
                >
                  {social[0]}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4
              style={{
                color: "#fff",
                fontSize: "16px",
                fontWeight: 600,
                marginBottom: "20px",
              }}
            >
              Navigation
            </h4>
            {["Accueil", "À Propos", "Solutions", "Projets", "Investir"].map(
              (link, i) => (
                <a
                  key={i}
                  href={`#${link.toLowerCase().replace("à ", "")}`}
                  style={{
                    display: "block",
                    color: "rgba(255,255,255,0.6)",
                    textDecoration: "none",
                    fontSize: "15px",
                    marginBottom: "12px",
                    transition: "color 0.2s ease",
                  }}
                >
                  {link}
                </a>
              )
            )}
          </div>

          <div>
            <h4
              style={{
                color: "#fff",
                fontSize: "16px",
                fontWeight: 600,
                marginBottom: "20px",
              }}
            >
              Solutions
            </h4>
            {[
              "Énergie Solaire",
              "Énergie Éolienne",
              "Électrolyse",
              "Stockage STEP",
              "Hydrogène Vert",
            ].map((link, i) => (
              <a
                key={i}
                href="#solutions"
                style={{
                  display: "block",
                  color: "rgba(255,255,255,0.6)",
                  textDecoration: "none",
                  fontSize: "15px",
                  marginBottom: "12px",
                  transition: "color 0.2s ease",
                }}
              >
                {link}
              </a>
            ))}
          </div>

          <div>
            <h4
              style={{
                color: "#fff",
                fontSize: "16px",
                fontWeight: 600,
                marginBottom: "20px",
              }}
            >
              Contact
            </h4>
            <p
              style={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "15px",
                marginBottom: "12px",
              }}
            >
              📧 contact@h2sol.fr
            </p>
            <p
              style={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "15px",
                marginBottom: "12px",
              }}
            >
              📞 +33 6 62 32 58 38
            </p>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "15px" }}>
              📍 France • Europe • Monde
            </p>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
            paddingTop: "30px",
          }}
        >
          <p
            style={{
              color: "rgba(255,255,255,0.4)",
              fontSize: "14px",
              margin: 0,
            }}
          >
            © 2025 H2SOL-Industry. Tous droits réservés.
          </p>
          <p
            style={{
              color: "rgba(255,255,255,0.4)",
              fontSize: "14px",
              margin: 0,
            }}
          >
            Fondé par Naji-Paul EL KAHY*
          </p>
        </div>
      </div>
    </footer>
  );
};

// ===== MAIN APP =====
export default function H2SolWebsite() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        
        *, *::before, *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        
        html {
          scroll-behavior: smooth;
        }
        
        body {
          margin: 0;
          padding: 0;
          min-height: 100vh;
          width: 100%;
        }
        
        #root {
          width: 100%;
          min-height: 100vh;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-30px); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(10px); }
        }
        
        @keyframes scrollDot {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(12px); opacity: 0.3; }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        ::selection {
          background: rgba(52, 211, 153, 0.3);
        }
        
        input:focus, textarea:focus, select:focus {
          border-color: #059669 !important;
        }

        @media (max-width: 768px) {
          .desktop-menu {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
        }
      `}</style>
      <div
        style={{
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          margin: 0,
          padding: 0,
          width: "100%",
          minHeight: "100vh",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        <Navigation />
        <Hero />
        <PartnersBar />
        <About />
        <Stats />
        <Solutions />
        <HydrogenTypes />
        <Projects />
        <Process />
        <Roadmap />
        <Invest />
        <Founder />
        <Contact />
        <Footer />
      </div>
    </>
  );
}